import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { pick, asList, fmtDateTime } from "@/lib/admin-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Search, PauseCircle, PlayCircle, Loader2 } from "lucide-react";

export function AdminUsers() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });

  const users = asList(data);
  const q = search.toLowerCase();
  const filtered = users.filter((u) =>
    !q || String(pick(u, ["name"], "")).toLowerCase().includes(q) || String(pick(u, ["email"], "")).toLowerCase().includes(q)
  );

  const setStatus = async (u, status) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id || u.user_id}/status`, { status });
      toast.success(status === "suspended" ? "Utente sospeso" : "Utente riattivato");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="px-4 sm:px-6 pt-5 space-y-4" data-testid="admin-users-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Utenti</h1>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <Input data-testid="users-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          className="h-12 pl-10 bg-white border-slate-200 rounded-full" placeholder="Cerca per nome o email..." />
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
      ) : filtered.length ? (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="users-list">
          {filtered.map((u) => {
            const id = u.id || u.user_id;
            const suspended = String(u.status).toLowerCase() === "suspended";
            return (
              <div key={id} data-testid={`user-row-${id}`} className="p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{pick(u, ["name"], "Utente")} <span className="font-normal text-slate-400 capitalize">· {u.role}</span></p>
                  <p className="text-xs text-slate-500 truncate">{pick(u, ["email"], "—")} · {fmtDateTime(pick(u, ["created_at"]))}</p>
                </div>
                {suspended ? (
                  <Button data-testid={`reactivate-user-${id}`} size="sm" disabled={busy} onClick={() => setStatus(u, "active")}
                    className="rounded-full bg-volt hover:bg-volt/90 text-white font-semibold shrink-0">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlayCircle className="w-4 h-4 mr-1" /> Riattiva</>}
                  </Button>
                ) : (
                  <Button data-testid={`suspend-user-${id}`} size="sm" variant="outline" disabled={busy} onClick={() => setStatus(u, "suspended")}
                    className="rounded-full font-semibold shrink-0">
                    <PauseCircle className="w-4 h-4 mr-1" /> Sospendi
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="users-empty">Nessun utente trovato.</p>
      )}
    </div>
  );
}
